import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Sparkles, ArrowRight, ArrowLeft, CheckCircle2, Loader2, FlaskConical, ClipboardCheck, GitBranch, Bell } from "lucide-react";
import { toast } from "sonner";

const TOTAL_STEPS = 5;
const MARKET_OPTIONS = ["FDA (US)", "EU MDR", "CDSCO (India)", "MDSAP"];

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [device, setDevice] = useState({ device_name: "", device_type: "", intended_use: "", markets: ["FDA (US)"], samd: false });
  const [projectId, setProjectId] = useState(null);
  const [classification, setClassification] = useState(null);
  const [ecrfSkeleton, setEcrfSkeleton] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const back = () => setStep(Math.max(1, step - 1));

  const doIntake = async () => {
    if (!device.device_name.trim() || !device.device_type.trim() || !device.intended_use.trim()) {
      return toast.error("Fill device name, type, and intended use");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/devices", device);
      setProjectId(data.project_id);
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || "Intake failed"); }
    finally { setBusy(false); }
  };

  const doClassify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/devices/${projectId}/classify`, {}, { timeout: 60000 });
      setClassification(data);
      // Stay on Step 3 so the user can review the FDA/EU/CDSCO verdict; they advance via the "Next" button.
    } catch (e) { toast.error(e.response?.data?.detail || "Classification failed"); }
    finally { setBusy(false); }
  };

  const doEcrf = async () => {
    setBusy(true);
    try {
      const desc = `${device.device_name} — ${device.device_type}. Intended use: ${device.intended_use}. ` +
                   `Regulatory context: ${device.markets.join(", ")}${device.samd ? " (SaMD)" : ""}. ` +
                   `Design a first-visit safety and enrolment eCRF with 5–8 essential fields including consent, demographics, primary safety observations, and device familiarity.`;
      const { data } = await api.post("/ecrf/ai-generate", { study_description: desc }, { timeout: 90000 });
      setEcrfSkeleton(data);
      setStep(5);
    } catch (e) {
      // AI is optional in the tour — still let the user finish
      if (e.response?.status === 502 || e.response?.status === 503) {
        toast.warning("AI form generator busy — skipping this step");
      } else {
        toast.error(e.response?.data?.detail || "AI form generation failed");
      }
      setStep(5);
    } finally { setBusy(false); }
  };

  const finish = () => {
    localStorage.setItem("vq_onboarded", "1");
    navigate(projectId ? `/devices/${projectId}` : "/dashboard");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-6">
      <div className="max-w-3xl w-full" data-testid="onboarding-container">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-[#0055FF]" />
            <span className="font-display font-extrabold text-xl text-slate-900">VitaQMS</span>
            <span className="vq-overline ml-2">First-time tour</span>
          </div>
          <button onClick={() => { localStorage.setItem("vq_onboarded", "1"); navigate("/dashboard"); }} data-testid="onboarding-skip"
                  className="text-xs text-slate-500 hover:text-slate-900 font-mono">Skip tour →</button>
        </div>

        <div className="vq-card p-8">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Step {step} of {TOTAL_STEPS}</span>
              <span className="text-[10px] font-mono text-emerald-600">· Safety by design</span>
            </div>
            <div className="h-1 bg-slate-100 rounded overflow-hidden">
              <div className="h-full bg-[#0055FF] transition-all" style={{ width: `${(step / TOTAL_STEPS) * 100}%` }} />
            </div>
          </div>

          {step === 1 && (
            <div data-testid="ob-step-1">
              <h1 className="font-display font-extrabold text-3xl text-slate-900 mb-3">Welcome. Let&apos;s put safety at the foundation.</h1>
              <p className="text-slate-600 mb-6">
                In the next five minutes you&apos;ll take a real device idea, get an AI-assisted regulatory classification,
                see the compliance modules that unlock for it, and generate a first clinical eCRF template — all with
                the 7 principles of good study design baked in.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <FeatureBadge icon={ShieldCheck} label="Real FDA / EU MDR / CDSCO logic" />
                <FeatureBadge icon={GitBranch} label="Auto-drafted quality workflows" />
                <FeatureBadge icon={FlaskConical} label="AI eCRF from your study idea" />
                <FeatureBadge icon={Bell} label="Live regulatory alerts + impact analysis" />
              </div>
              <Button onClick={() => setStep(2)} data-testid="ob-start"
                      className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold w-full h-12">
                Start the 5-minute tour <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {step === 2 && (
            <div data-testid="ob-step-2">
              <h2 className="font-display font-extrabold text-2xl text-slate-900 mb-2">Tell us about your device.</h2>
              <p className="text-slate-600 mb-6 text-sm">Real or hypothetical — we&apos;ll use it to run a real classification.</p>
              <div className="space-y-3">
                <div><Label className="text-xs font-semibold">Device name</Label>
                  <Input value={device.device_name} data-testid="ob-device-name"
                         onChange={(e) => setDevice({ ...device, device_name: e.target.value })}
                         className="mt-1.5" placeholder="e.g. VitaCyt insulin patch monitor" /></div>
                <div><Label className="text-xs font-semibold">Device type</Label>
                  <Input value={device.device_type} data-testid="ob-device-type"
                         onChange={(e) => setDevice({ ...device, device_type: e.target.value })}
                         className="mt-1.5" placeholder="Wearable / SaMD / IVD / Implantable…" /></div>
                <div><Label className="text-xs font-semibold">Intended use</Label>
                  <Textarea value={device.intended_use} data-testid="ob-intended-use"
                            onChange={(e) => setDevice({ ...device, intended_use: e.target.value })}
                            rows={3} className="mt-1.5"
                            placeholder="e.g. Continuous glucose monitoring in adults with T1D to inform insulin dosing." /></div>
                <div><Label className="text-xs font-semibold">Target markets</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {MARKET_OPTIONS.map((m) => (
                      <label key={m} className="inline-flex items-center gap-2 text-xs border border-slate-200 rounded px-3 py-1.5 cursor-pointer hover:bg-slate-50">
                        <Checkbox checked={device.markets.includes(m)}
                                  onCheckedChange={(v) => setDevice({ ...device, markets: v ? [...device.markets, m] : device.markets.filter((x) => x !== m) })}
                                  data-testid={`ob-market-${m.split(" ")[0]}`} />
                        <span>{m}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs pt-2">
                  <Checkbox checked={device.samd} onCheckedChange={(v) => setDevice({ ...device, samd: !!v })} data-testid="ob-samd" />
                  Software as a Medical Device (SaMD / AI-ML)
                </label>
              </div>
              <div className="mt-6 flex gap-2">
                <Button variant="outline" onClick={back}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
                <Button onClick={doIntake} disabled={busy} data-testid="ob-intake-submit"
                        className="bg-slate-900 hover:bg-[#0055FF] text-white flex-1">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : <>Next: Classify <ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="ob-step-3">
              <h2 className="font-display font-extrabold text-2xl text-slate-900 mb-2">Now let&apos;s classify it.</h2>
              <p className="text-slate-600 mb-6 text-sm">
                We&apos;ll run your device through Claude Sonnet 4.5 plus deterministic FDA / EU MDR / CDSCO rules.
                You&apos;ll see the regulatory pathway in about 15 seconds.
              </p>
              {!classification ? (
                <Button onClick={doClassify} disabled={busy} data-testid="ob-classify-btn"
                        className="bg-[#0055FF] hover:bg-[#0044cc] text-white w-full h-12">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Classifying…</> : <><Sparkles className="w-4 h-4 mr-2" /> Run classification</>}
                </Button>
              ) : (
                <ClassificationSummary c={classification} />
              )}
              {classification && (
                <div className="mt-6 flex gap-2">
                  <Button variant="outline" onClick={back}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
                  <Button onClick={() => setStep(4)} data-testid="ob-next-modules"
                          className="bg-slate-900 hover:bg-[#0055FF] text-white flex-1">Next: See what unlocks <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div data-testid="ob-step-4">
              <h2 className="font-display font-extrabold text-2xl text-slate-900 mb-2">This is the QMS spine that opens up.</h2>
              <p className="text-slate-600 mb-6 text-sm">
                Based on your classification, VitaQMS pre-wires the modules a real quality system needs.
                Each is human-review-gated with 21 CFR Part 11 electronic signatures.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <ModuleCard icon={GitBranch} title="CAPA + Deviations" />
                <ModuleCard icon={ShieldCheck} title="Risk Management (ISO 14971)" />
                <ModuleCard icon={ClipboardCheck} title="Change Control + e-signatures" />
                <ModuleCard icon={FlaskConical} title="Clinical eCRF (7 principles)" />
                <ModuleCard icon={Bell} title="Live regulatory alerts + impact" />
                <ModuleCard icon={CheckCircle2} title="Inspector portal + audit trails" />
              </div>
              <p className="text-xs text-slate-500 mb-4 italic">
                Optional bonus in the tour: generate a first eCRF template for a future clinical study around this device.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={back}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
                <Button onClick={doEcrf} disabled={busy} data-testid="ob-ecrf-btn"
                        className="bg-[#0055FF] hover:bg-[#0044cc] text-white flex-1">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate first eCRF</>}
                </Button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div data-testid="ob-step-5">
              <div className="w-14 h-14 rounded-full bg-emerald-500 text-white flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h2 className="font-display font-extrabold text-2xl text-slate-900 text-center mb-3">
                You just took an idea from concept to a compliance-ready plan — in five minutes.
              </h2>
              <p className="text-center text-slate-600 mb-6 text-sm">
                That&apos;s the point of VitaQMS: safety and regulation as your <b>foundation</b>, not a rewrite before submission.
              </p>
              {ecrfSkeleton && (
                <div className="border border-slate-200 rounded p-4 mb-6 bg-slate-50">
                  <div className="vq-overline mb-2">Your first eCRF, ready to review</div>
                  <div className="font-semibold text-slate-900">{ecrfSkeleton.name || "Study visit form"}</div>
                  <div className="text-xs text-slate-600 mt-1">
                    {(ecrfSkeleton.fields || []).length} field(s) · {(ecrfSkeleton.skip_rules || []).length} skip rule(s) ·
                    {(ecrfSkeleton.endpoints_suggested || []).length} endpoint(s) suggested
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <Button variant="outline" onClick={() => navigate("/clinical/forms")} data-testid="ob-open-clinical"
                        className="border-slate-900 text-slate-900">Open Clinical / eCRF <ArrowRight className="w-4 h-4 ml-2" /></Button>
                <Button onClick={finish} data-testid="ob-finish"
                        className="bg-slate-900 hover:bg-[#0055FF] text-white">
                  Open my device <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <Button variant="outline" onClick={() => {
                const b64 = ecrfSkeleton ? btoa(unescape(encodeURIComponent(JSON.stringify(ecrfSkeleton)))) : "";
                const url = `/onboarding/snapshot/${projectId}${b64 ? `?ecrf=${b64}` : ""}`;
                window.open(url, "_blank", "noopener");
              }} data-testid="ob-download-snapshot"
                      disabled={!projectId}
                      className="w-full border-[#0055FF] text-[#0055FF] hover:bg-blue-50 font-semibold">
                📄 Download your regulatory snapshot (PDF-ready)
              </Button>
            </div>
          )}
        </div>

        <p className="text-center text-[10px] font-mono text-slate-400 mt-4">
          Compliance is cheapest when it&apos;s earliest.
        </p>
      </div>
    </div>
  );
}

function FeatureBadge({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-xs text-slate-700 border border-slate-200 rounded p-2 bg-slate-50">
      <Icon className="w-4 h-4 text-[#0055FF] flex-shrink-0" />
      <span>{label}</span>
    </div>
  );
}
function ModuleCard({ icon: Icon, title }) {
  return (
    <div className="border border-slate-200 rounded p-3 hover:border-[#0055FF] transition">
      <Icon className="w-5 h-5 text-[#0055FF] mb-2" />
      <div className="text-sm font-semibold text-slate-900">{title}</div>
    </div>
  );
}
function ClassificationSummary({ c }) {
  const fda = c.fda || {}; const eu = c.eu_mdr || {}; const cdsco = c.cdsco || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <ClassCard label="FDA" value={fda.class || "—"} rationale={fda.rationale} />
        <ClassCard label="EU MDR" value={eu.class || "—"} rationale={eu.rationale} />
        <ClassCard label="CDSCO" value={cdsco.class || "—"} rationale={cdsco.rationale} />
      </div>
      {c.rule_verification?.requires_human_review && (
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">
          ⚠ Symbolic rule verification flagged this for human review — the AI verdict differs from the deterministic rule set on at least one market.
        </div>
      )}
    </div>
  );
}
function ClassCard({ label, value, rationale }) {
  return (
    <div className="border border-slate-200 rounded p-3">
      <div className="vq-overline text-[9px]">{label}</div>
      <div className="font-display font-bold text-xl text-slate-900 mt-1">{value}</div>
      {rationale && <div className="text-[10px] text-slate-500 mt-1 line-clamp-3">{rationale}</div>}
    </div>
  );
}
